// DECISION: se importa el módulo de API directamente, NO el barrel del feature
// ("../features/usuarios"), a pesar de que la convención general sea consumir
// los features por su barrel. El barrel también exporta `ListaUsuarios`, y eso
// arrastra la página entera -> components -> modals -> StateProvider, que a su
// vez importa este mismo fichero: un ciclo que dejaba `StateContext` a medio
// evaluar y hacía reventar `Toasts.render` con "Cannot read properties of
// undefined (reading 'toasts')" al arrancar la aplicación.
// `api/usuarios.api.js` solo depende de axios/HostService/AuthService, así que
// importarlo directo rompe el ciclo. No cambiar a la forma con barrel.
import UsuariosService from "../features/usuarios/api/usuarios.api";

/**
 * Fachada legacy de SOLO LECTURA sobre el feature "usuarios".
 *
 * Este módulo NO es código vivo: es un puente temporal para los 13
 * consumidores externos que hoy importan `listarUsuarios`/`listarUsuariosMap`
 * como lista de empleados para selects y filtros (9 usan listarUsuariosMap,
 * 4 usan listarUsuarios; ver TASK-001 en tasks/tasks.json). Ninguno de esos
 * 13 ficheros gestiona usuarios, así que no se migran a importar el feature
 * directamente: eso convertiría "migrar una vista" en "tocar 13 ficheros
 * ajenos sin necesidad funcional" (ver tasks/milestones.md, decisión 2).
 *
 * El código real vive en `src/features/usuarios/api/usuarios.api.js`. Este
 * fichero solo traduce las dos firmas legacy a `UsuariosService.list()`, sin
 * axios propio ni URL duplicada, para que exista un único punto hablando con
 * GET /usuarios. No añadir aquí nuevos métodos ni lógica de negocio: cualquier
 * funcionalidad nueva de gestión de usuarios va al feature, no a esta fachada.
 *
 * No borrar sin antes confirmar (grep) que los 13 consumidores han sido
 * migrados o han desaparecido.
 */
class UsuariosServiceLegacy {

    /**
     * Lista los usuarios activos como pares { id, nombre }, para poblar
     * selects/filtros de empleados en los 9 consumidores legacy.
     * @returns {Promise<Array<{id: number, nombre: string}>>} Lista de usuarios activos
     */
    static async listarUsuariosMap() {
        const usuarios = await UsuariosServiceLegacy.listarUsuarios();
        return usuarios.map((u) => ({
            id: u.id,
            nombre: u.username,
        }));
    }

    /**
     * Lista los usuarios activos, delegando en UsuariosService.list() del
     * feature nuevo. No se pasa incluirBajas: el backend ya filtra por
     * defecto solo activos, que es el comportamiento que esperan los 4
     * consumidores legacy.
     * @returns {Promise<Array>} Array de usuarios activos, en camelCase
     */
    static async listarUsuarios() {
        return UsuariosService.list();
    }
}

export default UsuariosServiceLegacy;
